import { html, LitElement, nothing } from "lit";
import { customElement, property } from "lit/decorators.js";

import { NAMESPACE } from "@/constants";
import { genRandSeed } from "@/utils";

import { EDIT_IDENTIFIER_DEFAULT_PROPS as DEFAULT_PROPS } from "./edit-identifier.defs";
import type {
  EditIdentifierProps as Props,
  EditIdentifierParts,
} from "./edit-identifier.types";
import { Auth } from "@saasbase-io/loginflow-websdk";
import { editIcon } from "@/assets/icons";

@customElement("sb-edit-identifier")
export class SbEditIdentifier extends LitElement implements Props {
  static readonly ROOT = "edit-identifier";
  static readonly ICON = "icon";

  static readonly TAG = `${NAMESPACE}-${SbEditIdentifier.ROOT}`;

  static readonly CLICK_EVENT = `${SbEditIdentifier.TAG}:click`;

  static readonly PARTS: Record<EditIdentifierParts, string> = {
    root: SbEditIdentifier.ROOT,
    icon: `${SbEditIdentifier.ROOT}-${SbEditIdentifier.ICON}`,
  };

  /* ------------------------------------------------------------ */

  /**
   * Substring used in generated part IDs.
   */
  @property({ type: String })
  seed = genRandSeed();

  /**
   * Custom ID used as the root part ID and as the base of subpart IDs.
   */
  @property({ type: String, attribute: "custom-id" })
  customId?: string;

  /**
   * Controls what the button renders: icon only, label only, or both.
   */
  @property({ type: String })
  identifier: string = DEFAULT_PROPS.identifier;

  /**
   * Event identifier forwarded to the authentication service on click.
   * When omitted, only the custom click event is dispatched.
   */
  @property({ type: String })
  event: string = "";

  /* ---------------------------------------- */

  /* ------------------------------------------------------------ */

  protected createRenderRoot() {
    return this;
  }

  /* ------------------------------------------------------------ */

  get rootId() {
    return this.customId ?? `${SbEditIdentifier.TAG}-${this.seed}`;
  }

  get iconId() {
    return `${this.rootId}-${SbEditIdentifier.ICON}`;
  }

  /* ------------------------------------------------------------ */

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener("click", this._handleClick);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener("click", this._handleClick);
  }

  /* ------------------------------------------------------------ */

  private _handleClick = () => {
    Auth.processLoginflowEvent({ event: this.event });
  };

  /* ---------------------------------------- */

  render() {
    return html`
      <div>
        <rtg-typography-p>${this.identifier}</rtg-typography-p>
        <div class="sb-flex sb-items-center">
          <rtg-button size="icon-sm" variant="link" onClick=${this._handleClick}
            >${editIcon()}</rtg-button
          >
        </div>
      </div>
    `;
  }
}
